import React, { cloneElement, useRef, type ReactElement } from 'react';
import { CSSTransition } from 'react-transition-group';
import type { CSSTransitionProps } from 'react-transition-group/CSSTransition';

/**
 * Wrapper component for `CSSTransition` that enables passing a ref directly to the child element.
 * This approach is required because React v19+ removed the `ReactDOM.findDOMNode` method, which `CSSTransition` previously relied on.
 * GH Issue: https://github.com/reactjs/react-transition-group/issues/918
 *
 * Note: The 'react-transition-group' library is no longer maintained and is incompatible with React v19+.
 * Consider migrating to alternatives such as 'react-transition-state': https://github.com/szhsin/react-transition-state#comparisons-with-react-transition-group
 */
type Props = CSSTransitionProps & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: ReactElement<any>;
};

export const CSSTransitionWithRef = ({ children, ...restProps }: Props) => {
  const ref = useRef<HTMLElement>(null);

  const setRef = (value: HTMLElement | null) => {
    ref.current = value;
  };

  return (
    <CSSTransition nodeRef={ref} {...restProps}>
      {cloneElement(children, { ref: setRef })}
    </CSSTransition>
  );
};
